import React, { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import {
  getUserAllocatedAssets,
  exportUserAllocatedAssets,
} from "../../services/AssetService";
import Pagination from "../Constant/Pagination";
import DebounceSmallSearchInput from "../Constant/DebounceSmallSearchInput";
import { FaDownload } from "react-icons/fa";

const UserAssetsTab = ({ userId }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [error, setError] = useState("");
  const [total, setTotal] = useState(0);

  const [param, setParam] = useState({
    query: "",
    page: 1,
    page_size: 10,
  });

  const loadAssets = async () => {
    setLoading(true);
    try {
      const response = await getUserAllocatedAssets(userId, {
        search: param.query,
        page: param.page,
        page_size: param.page_size,
      });

      const results = response.data?.results.results || []; // <-- FIXED: Correct results access
      setData(results);

      setTotal(response.data?.count || 0);
      setError("");
    } catch (err) {
      console.error("Failed to load assets", err);
      setError("Failed to load assets. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssets();
  }, [param, userId]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const response = await exportUserAllocatedAssets(userId, param.query);  // <-- Pass search term
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;

      const contentDisposition = response.headers["content-disposition"];
      let filename = "allocated-assets.xlsx";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match?.[1]) {
          filename = match[1];
        }
      }

      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      link.remove();

      toast.success("Export started successfully");
    } catch (err) {
      console.error("Export failed:", err);
      toast.error("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handlePageChange = (page) => {
    setParam((prev) => ({
      ...prev,
      page: page.selected + 1,
    }));
  };

  const handleSearchChange = (value) => {
    setParam((prev) => ({
      ...prev,
      query: value,
      page: 1,
    }));
  };

  return (
    <div className="card">
      <div className="px-4 py-3 d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h5 className="mb-0">Asset Allocated to User</h5>
        </div>

        <div className="d-flex flex-wrap align-items-center gap-2">
          <DebounceSmallSearchInput
            handleSearchChange={handleSearchChange}
            placeholder="Search assets..."
          />
          <button
            className="btn btn-success d-flex align-items-center gap-1 align-self-center"
            style={{ padding: "0.4rem 0.6rem" }}
            onClick={handleExport}
            disabled={exportLoading}
          >
            {exportLoading ? (
              <Spinner animation="border" size="sm" />
            ) : (
              <FaDownload />
            )}
            Export
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center my-4">
          <Spinner animation="border" />
        </div>
      ) : error ? (
        <div className="text-center my-4 text-danger">{error}</div>
      ) : data.length > 0 ? (
        <>
          <div className="table-responsive">
            <table className="table fs-6 mb-0 align-middle">
              <thead className="table-light custom-thead">
                <tr>
                  <th>#</th>
                  <th>Asset Name</th>
                  <th>Asset Number</th>
                  <th>Category</th>
                  <th>Subcategory</th>
                  <th>Status</th>
                  <th>Purchase Date</th>
                  <th>Purchase Cost</th>
                </tr>
              </thead>
              <tbody>
                {data.map((asset, index) => (
                  <tr key={asset.id}>
                    <td>{(param.page - 1) * param.page_size + index + 1}</td>
                    <td>{asset.asset_name}</td>
                    <td>{asset.asset_number}</td>
                    <td>{asset.category_name}</td>
                    <td>{asset.subcategory_name}</td>
                    <td>{asset.status}</td>
                    <td>{asset.purchase_date}</td>
                    <td>₹{asset.purchase_cost}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-4">
            <Pagination
              currentPage={param.page}
              recordsPage={param.page_size}
              dataLength={total}
              handlePagination={handlePageChange}
            />
          </div>
        </>
      ) : (
        <div className="text-center my-4">No assets assigned to this user.</div>
      )}
    </div>
  );
};

export default UserAssetsTab;
